import React from 'react';
import { useWishlist } from '../Context/WishlistContext';
import { useLocation } from 'react-router-dom';
import { Heart } from 'lucide-react';

const AddToWishlistButton = ({ 
  item, 
  className = '', 
  size = 'default',
  selections = null // { material, orientation, size, customWidth, customHeight, isCustom, price, etc. }
}) => {
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const location = useLocation();
  
  // Create unique identifier that includes selections
  // Use useMemo to ensure consistent calculation
  // Must be called before any early returns (React Hooks rule)
  const itemId = React.useMemo(() => {
    if (!item || !item.title) return null;
    if (selections) {
      // Include selections in the ID to make it unique
      const selectionStr = JSON.stringify(selections);
      return `${item.title}::${btoa(selectionStr)}`;
    }
    return item.title;
  }, [item, selections]);

  const inWishlist = itemId ? isInWishlist(itemId) : false;
  
  // Check if we're on a description page (contains /desc/)
  const isDescPage = location.pathname.includes('/desc/');

  if (!item || !item.title) {
    return null;
  }

  const handleClick = (e) => {
    e.stopPropagation();
    
    // Ensure we have a valid item with title and itemId
    if (!item || !item.title || !itemId) {
      console.error('[AddToWishlistButton] Cannot add item - missing item, title, or itemId:', { item, itemId });
      return;
    }
    
    console.log('[AddToWishlistButton] Clicked - item:', item);
    console.log('[AddToWishlistButton] Clicked - item.title:', item.title);
    console.log('[AddToWishlistButton] Clicked - itemId:', itemId);
    console.log('[AddToWishlistButton] Clicked - selections:', selections);
    
    if (inWishlist) {
      removeFromWishlist(itemId);
    } else {
      // Add item with selections - ensure we use the current item
      // Store the source route so we can navigate back to the correct page
      const sourceRoute = location.pathname; // e.g., /artprints/decorative/desc/4
      const itemToAdd = {
        ...item,
        wishlistId: itemId, // Store the unique ID
        selections: selections || null,
        sourceRoute: sourceRoute // Store where this item was added from
      };
      console.log('[AddToWishlistButton] Adding to wishlist with sourceRoute:', sourceRoute, itemToAdd);
      addToWishlist(itemToAdd);
    }
  };

  const sizeClasses = {
    small: 'w-8 h-8 p-1.5',
    default: 'w-10 h-10 p-2',
    large: 'w-12 h-12 p-3'
  };

  const iconSizes = {
    small: 'w-4 h-4',
    default: 'w-5 h-5',
    large: 'w-6 h-6'
  };

  // Larger icon sizes for category pages
  const categoryIconSizes = {
    small: 'w-5 h-5',
    default: 'w-6 h-6',
    large: 'w-7 h-7'
  };

  // Different styles for description pages vs category pages
  const buttonClasses = isDescPage
    ? `
        ${sizeClasses[size] || sizeClasses.default}
        ${inWishlist 
          ? 'bg-red-500 text-white hover:bg-red-600' 
          : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
        }
        rounded-full shadow-md hover:shadow-lg
        transition-all duration-300
        flex items-center justify-center
        ${className}
      `
    : `
        p-1
        ${inWishlist 
          ? 'text-red-500 hover:text-red-600' 
          : ''
        }
        transition-all duration-300
        flex items-center justify-center
        bg-transparent
        ${className}
      `;

  const heartClasses = isDescPage
    ? `${iconSizes[size] || iconSizes.default} ${inWishlist ? 'fill-current' : ''}`
    : `${categoryIconSizes[size] || categoryIconSizes.default} ${inWishlist ? 'fill-current' : ''} stroke-[3px]`;

  // For category pages, use gradient from light gray to dark black
  const gradientId = `heart-gradient-${itemId || 'default'}`;
  
  const heartStyle = !isDescPage && !inWishlist ? {
    stroke: `url(#${gradientId})`,
    fill: 'none',
    filter: 'drop-shadow(0 0 2px rgba(0, 0, 0, 0.3)) drop-shadow(0 0 1px rgba(0, 0, 0, 0.4))'
  } : !isDescPage && inWishlist ? {
    stroke: 'currentColor',
    fill: 'currentColor',
    filter: 'drop-shadow(0 0 2px rgba(0, 0, 0, 0.5))'
  } : {};

  return (
    <>
      {!isDescPage && !inWishlist && (
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f3f4f6" stopOpacity="1" />
              <stop offset="50%" stopColor="#e5e7eb" stopOpacity="1" />
              <stop offset="100%" stopColor="#d1d5db" stopOpacity="1" />
            </linearGradient>
          </defs>
        </svg>
      )}
      <button
        onClick={handleClick}
        className={buttonClasses}
        title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
        aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
      >
        <Heart 
          className={heartClasses}
          style={heartStyle}
        />
      </button>
    </>
  );
};

export default AddToWishlistButton;
